<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { FileTypeIcon } from '../file-type-icon';
import {
  FileKind,
  getFileThumbnailImageUrls,
  getFileThumbnailKind,
} from '../../utils/file';

type Props = {
  fileName: string;
  extension?: string;
  customPreviewUrl?: string | null;
  previewUrl?: string | null;
  fileUrl?: string | null;
  resolveImageUrl?: (url: string) => string;
};

const props = defineProps<Props>();

const VISIBILITY_ROOT_MARGIN = '200px';

const rootRef = ref<HTMLElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);
const isVisible = ref(false);
const failedSources = ref(new Set<string>());

watch(
  () => [props.customPreviewUrl, props.previewUrl, props.fileUrl],
  () => {
    failedSources.value = new Set();
  },
);

const isUsable = (source: string | null | undefined): source is string =>
  Boolean(source) && !failedSources.value.has(source as string);

const imageUrl = computed(
  () => getFileThumbnailImageUrls(props).find(isUsable) ?? null,
);

const imageSrc = computed(() =>
  imageUrl.value && props.resolveImageUrl
    ? props.resolveImageUrl(imageUrl.value)
    : imageUrl.value,
);

const videoUrl = computed(() =>
  !imageUrl.value &&
  getFileThumbnailKind(props) === FileKind.VIDEO &&
  isUsable(props.fileUrl)
    ? props.fileUrl
    : null,
);

const markFailed = (source: string) => {
  failedSources.value = new Set(failedSources.value).add(source);
};

const markFailedIfAlreadyBroken = () => {
  const image = imageRef.value;
  if (image && imageUrl.value && image.complete && image.naturalWidth === 0) {
    markFailed(imageUrl.value);
  }
};

let observer: IntersectionObserver | null = null;

onMounted(() => {
  markFailedIfAlreadyBroken();
  if (typeof IntersectionObserver === 'undefined' || !rootRef.value) {
    isVisible.value = true;
    return;
  }
  observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      isVisible.value = true;
      observer?.disconnect();
      observer = null;
    },
    { rootMargin: VISIBILITY_ROOT_MARGIN },
  );
  observer.observe(rootRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <div
    ref="rootRef"
    class="relative flex shrink-0 items-center justify-center overflow-hidden rounded-sm bg-surface"
  >
    <img
      v-if="imageUrl && imageSrc"
      ref="imageRef"
      :key="imageUrl"
      :src="imageSrc"
      alt=""
      loading="lazy"
      class="h-full w-full object-contain"
      @error="markFailed(imageUrl)"
    />
    <video
      v-else-if="isVisible && videoUrl"
      :src="`${videoUrl}#t=0.001`"
      preload="metadata"
      muted
      playsinline
      disablepictureinpicture
      aria-hidden="true"
      tabindex="-1"
      class="pointer-events-none h-full w-full object-contain"
      @error="markFailed(videoUrl)"
    />
    <FileTypeIcon
      v-else-if="!videoUrl"
      :file-name="fileName"
      :extension="extension"
    />
  </div>
</template>
